import { Box, Checkbox, FormControlLabel, Grid, Radio, RadioGroup, } from "@mui/material";
import React, { useState } from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Dialog } from "../../../../framework/Dialog/Dialog";
import { DialogButtons } from "../../../../framework/Dialog/DialogButtons";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskTypeChart } from "ui/Scripts/source/models/task/taskTypeChart";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { TaskTypeChartGroupingField } from "ui/Scripts/source/models/task/taskTypeChartGroupingField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { TaskChartGroupingFieldDynamicValueFieldType, TaskChartGroupingFieldGroupingType, TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { createDateField, modifyDateField } from "ui/Scripts/source/models/task/taskTypeCol";

export type GroupingFieldDialogItem = TaskTypeChartGroupingField & {
    title: string;
}

const GroupingFieldGroupingTypeOptions: IIdTitle[] = [
    IdTitle.createIdTitle(TaskChartGroupingFieldGroupingType.Value.toString(10), Translations.TaskTaskChartGroupingFieldGroupingTypeValue),
    IdTitle.createIdTitle(TaskChartGroupingFieldGroupingType.DayOfWeek.toString(10), Translations.TaskChartGroupingFieldGroupingTypeDayOfWeek),
    IdTitle.createIdTitle(TaskChartGroupingFieldGroupingType.Week.toString(10), Translations.TaskChartGroupingFieldGroupingTypeWeek),
    IdTitle.createIdTitle(TaskChartGroupingFieldGroupingType.Month.toString(10), Translations.TaskChartGroupingFieldGroupingTypeMonth),
    IdTitle.createIdTitle(TaskChartGroupingFieldGroupingType.Quartal.toString(10), Translations.TaskChartGroupingFieldGroupingTypeQuartal),
    IdTitle.createIdTitle(TaskChartGroupingFieldGroupingType.HalfYear.toString(10), Translations.TaskChartGroupingFieldGroupingTypeHalfYear),
    IdTitle.createIdTitle(TaskChartGroupingFieldGroupingType.Year.toString(10), Translations.TaskChartGroupingFieldGroupingTypeYear),
];

export const taskChartGroupingFieldGroupingTypeToString = (groupingType: TaskChartGroupingFieldGroupingType): string => {
    return GroupingFieldGroupingTypeOptions.find(option => option.id === groupingType.toString(10))?.title || "";
};

interface IChartGroupingFieldDialogProps {
    availableFields: IIdTitle[];
    taskType: ITaskEditTypeItem;
    selectedTaskTypeChart: ITaskTypeChart;
    item: GroupingFieldDialogItem;
    onSave: (item: GroupingFieldDialogItem) => void;
    onClose: () => void;
}

export const ChartGroupingFieldDialog = (props: IChartGroupingFieldDialogProps) => {
    const [state, setState] = useState<GroupingFieldDialogItem>({
        title: props.item.title,
        groupingType: props.item.groupingType ?? 0,
        field: props.item.field,
        sortOrderIsDesc: props.item.sortOrderIsDesc ?? false,
        dynamicValueFieldType: props.item.dynamicValueFieldType ?? TaskChartGroupingFieldDynamicValueFieldType.None
    });

    const isDateField = (props.taskType.formCols.find(c => c.field === state.field)?.dataType === TaskDataRowDataType.Date) || state.field.equalIgnoreCase(createDateField) || state.field.equalIgnoreCase(modifyDateField);
    
    const handleSave = async () => {
        props.onSave({
            ...state,
            groupingType: isDateField
                ? state.groupingType
                : TaskChartGroupingFieldGroupingType.Value,
            dynamicValueFieldType: isDateField && state.groupingType !== TaskChartGroupingFieldGroupingType.Value && state.groupingType !== TaskChartGroupingFieldGroupingType.Year
                ? state.dynamicValueFieldType
                : TaskChartGroupingFieldDynamicValueFieldType.None
        });
    };

    return (
        <Dialog
            id="chartGroupingFieldDialog"
            maxWidth="sm"
            title={Translations.ChartGroupingField}
            body={
                <>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.FormField} fullWidth margin="0">
                                <IdTitleDropdown
                                    selectedId={state.field}
                                    items={props.availableFields}
                                    onChange={(field: string) => setState(old => ({ ...old, field, title: props.availableFields.find(f => f.id === field)?.title || "" }))}
                                />
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.TaskChartGroupingFieldGroupingType} fullWidth>
                                {(isDateField
                                    ? <IdTitleDropdown
                                        selectedId={state.groupingType.toString(10)}
                                        items={GroupingFieldGroupingTypeOptions}
                                        onChange={(field: string) => {
                                            setState((old) => ({ ...old, groupingType: parseInt(field, 10) || 0 }));
                                        }}
                                    />
                                    : Translations.TaskTaskChartGroupingFieldGroupingTypeValue
                                )}
                            </FormField>
                        </Grid>
                    </GridRow>
                    <GridRow>
                        <Grid item sm={12}>
                            <FormField title={Translations.Sorting} fullWidth>
                                <RadioGroup
                                    row={true}
                                    onChange={(e) => setState(old => ({ ...old, sortOrderIsDesc: e.target.value === "desc" }))}
                                    value={state.sortOrderIsDesc ? "desc" : "asc"}
                                >
                                    <FormControlLabel
                                        value="asc"
                                        control={<Radio/>}
                                        label={Translations.Ascending}
                                        className="radio"
                                    />
                                    <FormControlLabel
                                        value="desc"
                                        control={<Radio/>}
                                        label={Translations.Descending}
                                        className="radio"
                                        sx={{ marginRight: "25px" }}
                                    />
                                </RadioGroup>
                            </FormField>
                        </Grid>
                    </GridRow>
                    {isDateField && state.groupingType !== TaskChartGroupingFieldGroupingType.Value && state.groupingType !== TaskChartGroupingFieldGroupingType.Year &&
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title="" fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                title={Translations.TaskChartGroupingFieldDynamicValueFieldTypeYearly}
                                                sx={{ paddingBottom: 0, paddingTop: 0 }}
                                                //sx={getCheckBoxSizeSx(typeCol.class)}
                                                checked={state.dynamicValueFieldType !== TaskChartGroupingFieldDynamicValueFieldType.None}
                                                onChange={(_e, checked) => setState(old => ({ ...old, dynamicValueFieldType: checked ? TaskChartGroupingFieldDynamicValueFieldType.Yearly : TaskChartGroupingFieldDynamicValueFieldType.None })) }
                                            />
                                        }
                                        label={Translations.TaskChartGroupingFieldDynamicValueFieldTypeYearly}
                                        className="checkbox"
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    }
                </>
            }
            actions={
                <Box display="flex" justifyContent="flex-end" width="100%">
                    <DialogButtons
                        saveTitle={Translations.OK}
                        onCancel={props.onClose}
                        onSave={handleSave}
                    />
                </Box>
            }
            onClose={props.onClose}
        />
    );
};
